import { Framework, type IHistoryItem } from '@/framework';
import { applyDifferentProps, getDifferentProps } from '@/utils/layer-kit';
import { LayerType } from '@/version-types';
import {
    HistoryType,
    type IHistoryConvertImageToBackgroundData,
    type IHistoryExpendImageAndChangeBgData,
} from './type';

const getExpendDiff = (
    newLayer: Parameters<typeof getDifferentProps>[0],
    oldLayer: Parameters<typeof getDifferentProps>[1],
    newBgLayer: Parameters<typeof getDifferentProps>[0],
    oldBgLayer: Parameters<typeof getDifferentProps>[1],
) => {
    const diffInfo = getDifferentProps(newLayer, oldLayer, undefined, new Set(['parentId']));
    return getDifferentProps(newBgLayer, oldBgLayer, diffInfo, new Set(['parentId']));
};

export class ImageHistory extends Framework {
    constructor(identity: string) {
        super(identity);
        this.registerConvertImageToBackground();
        this.registerExpendImageAndChangeBg();
    }

    /** 转换图片为背景 */
    private registerConvertImageToBackground() {
        this.framework.service.history.register(HistoryType.CONVERT_IMAGE_TO_BACKGROUND, {
            owner: 'layer/image',
            collaboration: 'supported',
            onRedoUndo: (
                historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>,
                isRedo: boolean,
                isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isRedo) {
                    this.framework.service.frame.delLayer(oldLayer.id);
                    this.framework.service.frame.addOrUpdateLayer(newBgLayer);
                    this.framework.service.frame.delActiveLayer();
                }
                if (isUndo) {
                    this.framework.service.frame.addOrUpdateLayer(oldBgLayer);
                    oldLayer.parentId = undefined;
                    this.framework.service.frame.addOrUpdateLayer(oldLayer);
                    this.framework.service.frame.setActiveLayer(oldLayer.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>) => {
                return {
                    message: [historyItem.data.data],
                };
            },
            onBeforeRedoUndo: (
                historyItem: IHistoryItem<IHistoryConvertImageToBackgroundData>,
                isRedo,
                isUndo,
                store,
            ) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isUndo) {
                    historyItem.isDirty =
                        !!this.framework.service.flatLayer.getLayer(oldLayer.id) ||
                        !this.framework.service.flatLayer.getLayer(oldBgLayer.id);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty =
                            !this.framework.service.flatLayer.getLayer(oldLayer.id) ||
                            !this.framework.service.flatLayer.getLayer(newBgLayer.id);
                    }
                }
            },
            onMessage: (message: [IHistoryConvertImageToBackgroundData], isRedo, isUndo, isWrite) => {
                const { item } = message[0];
                const { oldLayer, newBgLayer, oldBgLayer } = item;
                if (isRedo || isWrite) {
                    this.framework.service.frame.tryRemoveActiveLayer(oldLayer.id);
                    this.framework.service.frame.delLayer(oldLayer.id);
                    this.framework.service.frame.addOrUpdateLayer(newBgLayer);
                }
                if (isUndo) {
                    this.framework.service.frame.addOrUpdateLayer(oldBgLayer);
                    oldLayer.parentId = undefined;
                    this.framework.service.frame.addOrUpdateLayer(oldLayer);
                }
            },
        });
    }

    /**
     * 扩展图片并更换背景
     *
     * 1.从官网带图片进入编辑器，改尺寸后会同步修改背景尺寸
     * 示例：
     * http://localhost:3003/design/?createEmptyPoster=true&modelId=0&unit=px&width=600&height=600&open=https%3A%2F%2Fxiuxiu-pro-pre.meitudata.com%2Fposters%2Fcefc32facab3523618ae44cd9ccd7088.png&feature=image_expand
     *
     * 2.普通扩图功能历史记录写入的是默认格式，调用的是
     * imageLayer.processAIExpendImage 处理历史记录
     */
    private registerExpendImageAndChangeBg() {
        this.framework.service.history.register(HistoryType.EXPEND_IMAGE_AND_CHANGE_BG, {
            owner: 'layer/image',
            collaboration: 'supported',
            isLikeDefault: true,
            onRedoUndo: (
                historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>,
                isRedo: boolean,
                _isUndo: boolean,
            ) => {
                if (historyItem.isDirty) return;
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer, newLayer, diffInfo } = item;
                if (diffInfo) {
                    Object.keys(diffInfo).forEach((layerId) => {
                        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
                        if (!layer) return;
                        applyDifferentProps(layer, diffInfo[layerId]);
                        this.framework.service.frame.addOrUpdateLayer(layer);
                    });
                } else {
                    this.framework.service.frame.addOrUpdateLayer(isRedo ? newLayer : oldLayer);
                    this.framework.service.frame.addOrUpdateLayer(isRedo ? newBgLayer : oldBgLayer);
                }
                const activeLayer = isRedo ? newLayer : oldLayer;
                if (activeLayer.layerType !== LayerType.MAIN_LAYER) {
                    this.framework.service.frame.setActiveLayer(activeLayer.id);
                }
            },
            onAfterWrite: (historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>) => {
                const { oldLayer, newLayer, oldBgLayer, newBgLayer } = historyItem.data.data.item;
                this.framework.service.frame.tryRefreshActiveLayer(newLayer.id);
                return {
                    message: [getExpendDiff(newLayer, oldLayer, newBgLayer, oldBgLayer), newLayer.id],
                };
            },
            onAfterRedoUndo: (historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>) => {
                const { newLayer, diffInfo } = historyItem.data.data.item;
                return {
                    message: [diffInfo || {}, newLayer.id],
                };
            },
            onBeforeRedoUndo: (
                historyItem: IHistoryItem<IHistoryExpendImageAndChangeBgData>,
                isRedo,
                isUndo,
                store,
            ) => {
                const { item } = historyItem.data.data;
                const { oldLayer, newBgLayer, oldBgLayer, newLayer } = item;
                if (isUndo) {
                    historyItem.isDirty =
                        !this.framework.service.flatLayer.getLayer(newLayer.id) ||
                        !this.framework.service.flatLayer.getLayer(newBgLayer.id);
                    historyItem.data.data.item.diffInfo = getExpendDiff(oldLayer, newLayer, oldBgLayer, newBgLayer);
                    store.set(historyItem.cursor, {
                        isDirty: historyItem.isDirty,
                    });
                }
                if (isRedo) {
                    const beforeData = store.get(historyItem.cursor);
                    if (!beforeData) {
                        historyItem.isDirty = true;
                        return;
                    }
                    historyItem.isDirty = beforeData.isDirty;
                    if (!historyItem.isDirty) {
                        historyItem.isDirty =
                            !this.framework.service.flatLayer.getLayer(oldLayer.id) ||
                            !this.framework.service.flatLayer.getLayer(oldBgLayer.id);
                    }
                    historyItem.data.data.item.diffInfo = getExpendDiff(newLayer, oldLayer, newBgLayer, oldBgLayer);
                }
            },
            onMessage: (message: [Record<string, object>, string]) => {
                const [diffInfo, activeLayerId] = message;
                Object.keys(diffInfo || {}).forEach((layerId) => {
                    const layer = this.framework.service.flatLayer.getLayer(layerId, true);
                    if (!layer) return;
                    applyDifferentProps(layer, diffInfo[layerId]);
                    this.framework.service.frame.addOrUpdateLayer(layer);
                    this.framework.service.frame.tryRefreshActiveLayer(layerId);
                });
                this.framework.service.frame.tryRefreshActiveLayer(activeLayerId);
            },
        });
    }
}
